/**
 * PdfPane — presentational wrapper around useRasterViewport.js (Submittal Review Tool, Phase 3).
 *
 * A toolbar (zoom in/out, fit-to-width, page prev/next + "n / N" when multi-page, a collapsible
 * thumbnail rail toggle, and a slot for caller-supplied extra controls like a file switcher) over
 * a raster+pan/zoom PDF canvas. No shared module-level state — every instance owns its own
 * useRasterViewport hook call, so three PdfPane instances can sit side by side (the Review
 * Workspace's package/drawings/specs panes — see docs/SUBMITTAL_REVIEW_PLAN.md Phase 4) without
 * colliding.
 *
 * All colors are `var(--aj-*)` tokens — see docs/DARK_MODE.md. This component does not draw
 * annotations; that's Phase 5's job, layered on top via the exposed screenToFrac/fracToScreen
 * transforms and the containerRef/canvasRef this hook returns.
 */
import { useEffect, useRef, useState } from 'react'
import { useRasterViewport } from './useRasterViewport.js'

export default function PdfPane({ pdfUrl, title, onPageChange, extraToolbarContent, className }) {
  const {
    containerRef, canvasRef,
    loading, error,
    zoomIn, zoomOut, fitToWidth,
    beginPan, movePan, endPan,
    pageNum, numPages, goToPage, nextPage, prevPage,
    thumbnails, ensureThumbnails,
  } = useRasterViewport({ pdfUrl })

  const [railOpen, setRailOpen] = useState(false)
  const draggingRef = useRef(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (typeof onPageChange === 'function') onPageChange(pageNum) }, [pageNum])
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (railOpen) ensureThumbnails() }, [railOpen, pdfUrl])

  function onPointerDown(e) {
    if (e.button !== 0 && e.button !== 1) return
    draggingRef.current = true
    beginPan(e.clientX, e.clientY)
    try { e.currentTarget.setPointerCapture(e.pointerId) } catch { /* ignore */ }
  }
  function onPointerMove(e) { if (draggingRef.current) movePan(e.clientX, e.clientY) }
  function onPointerUp(e) {
    if (!draggingRef.current) return
    draggingRef.current = false
    endPan()
    try { e.currentTarget.releasePointerCapture(e.pointerId) } catch { /* ignore */ }
  }

  return (
    <div className={className} style={S.root}>
      <div style={S.toolbar}>
        {title != null && <span style={S.title} title={typeof title === 'string' ? title : undefined}>{title}</span>}
        <button type="button" style={S.btn} onClick={zoomOut} title="Zoom out">−</button>
        <button type="button" style={S.btn} onClick={zoomIn} title="Zoom in">+</button>
        <button type="button" style={S.btn} onClick={fitToWidth} title="Fit to width">Fit</button>
        {numPages > 1 && (
          <>
            <span style={S.divider} />
            <button type="button" style={S.btn} onClick={prevPage} disabled={pageNum <= 1} title="Previous page">‹</button>
            <span style={S.pageNum}>{pageNum} / {numPages}</span>
            <button type="button" style={S.btn} onClick={nextPage} disabled={pageNum >= numPages} title="Next page">›</button>
            <button
              type="button"
              style={{ ...S.btn, ...(railOpen ? S.btnActive : null) }}
              onClick={() => setRailOpen(o => !o)}
              title={railOpen ? 'Hide pages' : 'Show pages'}
            >▤</button>
          </>
        )}
        <div style={{ flex: 1 }} />
        {extraToolbarContent}
      </div>
      <div style={S.body}>
        {railOpen && numPages > 1 && (
          <div style={S.rail}>
            {thumbnails.length === 0 && <div style={S.railHint}>Loading pages…</div>}
            {thumbnails.map(t => (
              <button
                key={t.pageNum}
                type="button"
                onClick={() => goToPage(t.pageNum)}
                style={{ ...S.thumbBtn, borderColor: t.pageNum === pageNum ? 'var(--aj-accent)' : 'var(--aj-border-mid)' }}
              >
                <img src={t.dataUrl} alt={'Page ' + t.pageNum} style={S.thumbImg} />
                <span style={S.thumbLbl}>{t.pageNum}</span>
              </button>
            ))}
          </div>
        )}
        <div
          ref={containerRef}
          style={S.canvasWrap}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerUp}
        >
          <canvas ref={canvasRef} style={S.canvas} />
          {loading && <div style={S.overlay}>Loading…</div>}
          {!loading && error && <div style={{ ...S.overlay, color: '#dc2626' }}>{error}</div>}
          {!loading && !error && !pdfUrl && <div style={S.overlay}>No document</div>}
        </div>
      </div>
    </div>
  )
}

const S = {
  // flex:1 (not height:'100%') deliberately — a percentage height requires the parent's own
  // height to be "definite," which gets unreliable once this sits a couple of flex levels deep
  // next to a sibling with its own percentage sizing (see SubmittalReviewModal's paneList fix,
  // 2026-08). flex:1 lets a plain flex-container parent size this correctly either way.
  root: { display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0, flex: 1, background: 'var(--aj-bg-surface)' },
  toolbar: {
    display: 'flex', alignItems: 'center', gap: 4, padding: '6px 8px',
    borderBottom: '0.5px solid var(--aj-border)', background: 'var(--aj-bg-elevated)', flexShrink: 0,
  },
  title: {
    fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, color: 'var(--aj-text)',
    marginRight: 6, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 200,
  },
  divider: { width: '0.5px', height: 18, background: 'var(--aj-border-mid)', margin: '0 2px', flexShrink: 0 },
  btn: {
    fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, lineHeight: 1,
    minWidth: 24, height: 24, padding: '0 6px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)',
    background: 'transparent', color: 'var(--aj-text-mid)', cursor: 'pointer',
  },
  btnActive: { background: 'var(--aj-bg-muted)', color: 'var(--aj-text)' },
  pageNum: { fontFamily: '"DM Mono",monospace', fontSize: 11, color: 'var(--aj-text-mid)', padding: '0 4px', whiteSpace: 'nowrap' },
  body: { flex: 1, minHeight: 0, display: 'flex', position: 'relative' },
  rail: {
    width: 96, flexShrink: 0, overflowY: 'auto', borderRight: '0.5px solid var(--aj-border)',
    background: 'var(--aj-bg-subtle)', padding: 6, display: 'flex', flexDirection: 'column', gap: 6,
  },
  railHint: { fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-text-muted)', textAlign: 'center', padding: '8px 0' },
  thumbBtn: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, padding: 3,
    background: 'var(--aj-bg-elevated)', border: '2px solid var(--aj-border-mid)', borderRadius: 4, cursor: 'pointer',
  },
  thumbImg: { width: '100%', height: 'auto', display: 'block', border: '0.5px solid var(--aj-border)', borderRadius: 2 },
  thumbLbl: { fontFamily: '"DM Mono",monospace', fontSize: 9, color: 'var(--aj-text-muted)' },
  canvasWrap: { flex: 1, minWidth: 0, minHeight: 0, position: 'relative', overflow: 'hidden', background: 'var(--aj-bg-muted)', cursor: 'grab', touchAction: 'none' },
  canvas: { position: 'absolute', top: 0, left: 0, display: 'block' },
  overlay: {
    position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, color: 'var(--aj-text-muted)', textAlign: 'center', padding: 20,
    background: 'var(--aj-bg-muted)', pointerEvents: 'none',
  },
}
